import { motion } from 'framer-motion';
import { ArrowDown, HandHeart, HeartHandshake } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BRAND } from '@/lib/site';

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden pt-28 pb-16 md:pt-36 md:pb-24 isolate"
    >
      {/* Background layers */}
      <div className="pointer-events-none absolute inset-0 -z-30 bg-gradient-to-b from-saffron-50 via-cream to-cream dark:from-[#21121A] dark:via-[#1A0F14] dark:to-[#1A0F14]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-20 h-[680px]">
        <div className="absolute left-1/2 top-1/2 h-[820px] w-[820px] -translate-x-1/2 -translate-y-1/2 bg-saffron-glow opacity-90 blur-[2px]" />
      </div>
      <div className="pointer-events-none absolute inset-0 -z-20 bg-mandala bg-center bg-no-repeat opacity-30 animate-slow-spin" />
      <div className="pointer-events-none absolute inset-0 -z-10 grain" />

      {/* Floating petals */}
      {[
        { top: '12%', left: '6%', size: 14, delay: 0 },
        { top: '20%', right: '8%', size: 22, delay: 1.2 },
        { top: '60%', left: '4%', size: 18, delay: 0.6 },
        { top: '70%', right: '5%', size: 26, delay: 1.8 },
        { top: '40%', left: '50%', size: 10, delay: 2.2 },
      ].map((p, i) => (
        <motion.div
          key={i}
          aria-hidden
          style={{ ...p, position: 'absolute' }}
          animate={{ y: [0, -16, 0], rotate: [0, 12, 0] }}
          transition={{ duration: 8 + i, repeat: Infinity, ease: 'easeInOut', delay: p.delay }}
          className="text-saffron-500/70 dark:text-saffron-400/70"
        >
          <svg width={p.size} height={p.size} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C9 7 6 9 2 10c4 1 7 3 10 8 3-5 6-7 10-8-4-1-7-3-10-8z" />
          </svg>
        </motion.div>
      ))}

      <div className="container-x relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-12">
          {/* Left: copy */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="ornament-line eyebrow"
            >
              <span>{BRAND.tagline}</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="h-display mt-6 text-5xl sm:text-6xl lg:text-7xl xl:text-[5.4rem] font-bold leading-[1.02] text-maroon-700 dark:text-foreground"
            >
              Serving <span className="saffron-text italic">Humanity</span>
              <br />
              Across <span className="relative inline-block">
                India
                <motion.span
                  aria-hidden
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 1, duration: 1, ease: 'easeOut' }}
                  className="absolute -bottom-2 left-0 right-0 h-[6px] origin-left rounded-full bg-gradient-to-r from-saffron-500 via-gold-400 to-saffron-500"
                />
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-7 max-w-xl font-serif text-xl md:text-2xl leading-relaxed text-foreground/75"
            >
              For every plate of warm khichdi, every notebook handed to a first-generation learner,
              every grandmother helped to her feet — there is a quiet vow we keep:
              <span className="font-devanagari text-maroon-700 dark:text-saffron-300 ml-1">
                नर सेवा, नारायण सेवा.
              </span>
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              <Button
                asChild
                size="lg"
                data-testid="hero-donate-btn"
                className="group rounded-full bg-saffron-500 hover:bg-saffron-600 px-7 py-6 text-base text-white shadow-[0_18px_40px_-12px_rgba(255,103,31,0.7)] transition-all hover:-translate-y-0.5"
              >
                <a href="#donate" className="inline-flex items-center gap-2">
                  <HandHeart className="h-5 w-5" />
                  Donate Now
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                data-testid="hero-whatsapp-btn"
                className="rounded-full border-maroon-700 text-maroon-700 hover:bg-maroon-700 hover:text-white dark:border-saffron-300 dark:text-saffron-200 dark:hover:bg-saffron-500/10 px-7 py-6 text-base"
              >
                <a
                  href={BRAND.whatsappHref}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2"
                >
                  <HeartHandshake className="h-5 w-5" />
                  Talk on WhatsApp
                </a>
              </Button>
            </motion.div>

            {/* Trust strip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.7 }}
              className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs uppercase tracking-[0.22em] text-foreground/60"
            >
              <span>80G eligible</span>
              <span className="h-1 w-1 rounded-full bg-saffron-500" />
              <span>Audited annually</span>
              <span className="h-1 w-1 rounded-full bg-saffron-500" />
              <span>{BRAND.reachLine}</span>
            </motion.div>
          </div>

          {/* Right: logo emblem */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="relative mx-auto aspect-square w-[78%] max-w-md"
            >
              {/* radial saffron rays */}
              <div className="absolute inset-0 -z-10 rounded-full bg-saffron-glow scale-125 animate-pulse-glow" />
              <div className="absolute inset-0 -z-10 rounded-full bg-gold-glow scale-110 opacity-70" />

              {/* rotating ornament ring */}
              <svg
                aria-hidden
                viewBox="0 0 400 400"
                className="absolute inset-0 -z-10 h-full w-full animate-slow-spin"
              >
                <defs>
                  <linearGradient id="ringG" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#FF671F" />
                    <stop offset="100%" stopColor="#6B1A24" />
                  </linearGradient>
                </defs>
                <circle
                  cx="200"
                  cy="200"
                  r="186"
                  fill="none"
                  stroke="url(#ringG)"
                  strokeWidth="1"
                  strokeDasharray="2 8"
                  opacity="0.55"
                />
                <circle
                  cx="200"
                  cy="200"
                  r="170"
                  fill="none"
                  stroke="#D4AF37"
                  strokeWidth="0.6"
                  strokeDasharray="1 6"
                  opacity="0.55"
                />
              </svg>

              <img
                src={BRAND.logo}
                alt="ADI Foundation logo"
                className="relative h-full w-full object-contain mix-blend-multiply animate-gentle-float drop-shadow-[0_24px_60px_rgba(107,26,36,0.35)]"
              />
            </motion.div>
          </div>
        </div>

        {/* scroll indicator */}
        <motion.a
          href="#about"
          aria-label="Scroll down"
          data-testid="hero-scroll-indicator"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="mx-auto mt-16 hidden md:flex w-fit items-center gap-2 text-xs uppercase tracking-[0.28em] text-foreground/60 hover:text-saffron-600"
        >
          <span>Begin the story</span>
          <motion.span
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.6, repeat: Infinity }}
          >
            <ArrowDown className="h-4 w-4" />
          </motion.span>
        </motion.a>
      </div>
    </section>
  );
}
